import React from 'react';
import WideContent from 'core/layout/content/wide-content';
import WithAuthenticatedPage from 'core/auth/components/with-authenticated-page';
import Header from 'ui/atoms/header';
import Translate from 'ui/atoms/translate';
import BoxedContent from 'ui/molecules/boxed-content';
import DashboardInvestmentDetails from 'src/apps/investor/shared/dashboard-investment-details';
import LoadingRing from 'ui/atoms/loading-ring';
import { AppType } from 'core/auth/types';
import useInvestment from 'hooks/use-investment';
import Section from 'ui/atoms/section';
import InvestorInvestmentActionButtons from 'src/apps/investor/shared/investment-action-buttons';
import { getWalletAddressForNetwork } from 'ui/helper/networks';
import BoxedInvestmentError from 'components/Investment/BoxedInvestmentError';
import Date from 'ui/atoms/date';
import Link from 'core/routing/link';
import InfoGrid, { InfoField } from 'src/ui/molecules/info-grid';
import PlaceholderFallback from 'src/ui/atoms/placeholder-fallback';
import { hideCentralCharacters } from 'ui/helper/hideCharacters';
import { useInvestorSelector } from 'apps/investor/resources/investor/hooks';
import Currency from 'ui/atoms/currency';
import Grid, { Col } from 'src/ui/atoms/grid';
import InvestmentDocumentsTable from './investment-documents-table';

const InvestorInvestmentDetailsPage = (props: { id: string }) => {
  const { id } = props;
  const { investment, investorStatus, token, loading, error } = useInvestment(id);
  const { investor } = useInvestorSelector();

  const website = token?.website;

  if (error) return <BoxedInvestmentError error={error} />;

  if (loading || !investment || !investorStatus || !token) {
    return <LoadingRing />;
  }

  return (
    <>
      <WideContent
        title={
          <Translate
            name={'dashboardInvestorInvestmentDetails.title'}
            args={[token?.name, (_, key) => <Date>{investment.signedAt}</Date>]}
          />
        }
      >
        <Section spacing="large">
          <BoxedContent releaseSpaceWhenTitleIsEmpty={true}>
            <DashboardInvestmentDetails
              investment={investment}
              website={website}
              investorStatus={investorStatus}
              walletAddress={
                investment.wallet &&
                token.tokenizationInfo &&
                getWalletAddressForNetwork(token.tokenizationInfo.networkType, investment.wallet)
              }
            />
            <InvestorInvestmentActionButtons status={investorStatus} invitationId={investment.id} />
          </BoxedContent>
        </Section>
        {!token?.ewpg && (
          <Section>
            <Grid spacing="large">
              <Col width={6}>
                <BoxedContent title={<Translate name="studioInvestmentTaxData.title" />}>
                  <InfoGrid columns={1}>
                    <InfoField title={<Translate name="studioInvestmentTaxData.taxNumber" />}>
                      <PlaceholderFallback>
                        {investor?.person.taxInformation?.taxIdentificationNumber &&
                          hideCentralCharacters(investor?.person.taxInformation?.taxIdentificationNumber, 2, -2)}
                      </PlaceholderFallback>
                    </InfoField>
                    <InfoField title={<Translate name="studioInvestmentTaxData.exemptionOrderAmount" />}>
                      <PlaceholderFallback>
                        {investment?.exemptionOrderAmount && <Currency>{investment?.exemptionOrderAmount}</Currency>}
                      </PlaceholderFallback>
                    </InfoField>
                    <InfoField title={<Translate name="studioInvestmentTaxData.lastChangeExemptionOrder" />}>
                      <PlaceholderFallback>
                        {investment?.exemptionOrderDate && <Date>{investment?.exemptionOrderDate}</Date>}
                      </PlaceholderFallback>
                    </InfoField>
                  </InfoGrid>
                </BoxedContent>
              </Col>
            </Grid>
          </Section>
        )}
        <Section>
          <Header size="small">
            <Translate name="dashboardInvestorInvestmentDetails.newsAndDocuments" />
          </Header>
          <InvestmentDocumentsTable investmentId={investment.id} />
        </Section>
        <Link to={`/dashboard/investorProduct/${token.id}`} icon="arrow-left">
          <Translate as="span" name="dashboardInvestorInvestmentDetails.backToProductDetails" />
        </Link>
      </WideContent>
    </>
  );
};

export default WithAuthenticatedPage(InvestorInvestmentDetailsPage, AppType.INVESTOR);
